/*
 * Tipurile raspunsurilor, intr-un singur loc, dupa docs/shop-api-contract.md.
 *
 * Regula fisierului: campurile scrise pe campuri in contract sunt obligatorii;
 * campurile pe care contractul le descrie doar in proza (cosul, detaliul unei
 * cereri) sunt optionale si au variante de nume. Nu e neglijenta,
 * e ce s-a invatat din implementarea fara framework: api-ul intoarce azi
 * `state` si maine `status`, iar un tip prea ingust nu opreste asta, doar mute
 * eroarea din ecran in compilator. Cand forma se fixeaza in api, variantele se
 * sterg de aici si compilatorul arata singur toate locurile de curatat.
 *
 * Ce NU e aici: `company_id` ca parametru de cerere. Contractul il interzice pe
 * orice ruta, deci nu exista tip care sa-l permita.
 */

/* -------------------------------------------------- forma raspunsului, §0 */

export interface ApiSuccess<T> {
  status: 'SUCCESS';
  data: T;
}

export interface ApiFailure {
  status: 'ERROR';
  /** Text pentru ecran. Se afiseaza asa cum vine. */
  message: string;
  /** Slug pentru cod. Ramificarile se fac pe el, nu pe `message`. */
  code: string;
  data?: { fields?: Record<string, string> } | null;
}

export type ApiEnvelope<T> = ApiSuccess<T> | ApiFailure;

/** Paginarea din §0: `{items, total, page, per_page}`. */
export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
}

/**
 * Banii: zecimal ca sir, moneda separat. Contractul interzice float, deci
 * `amount` e `string` si nu se converteste niciodata la `number`.
 */
export interface Money {
  amount: string;
  currency: string;
}

/* ------------------------------------------------------ autentificare, §1 */

export interface ShopUser {
  id: number;
  name: string;
  email: string;
  /** Vine din token; se afiseaza, nu se trimite inapoi niciodata. */
  company_id: number;
}

/** Corpul intors identic de login, refresh si sso/exchange. */
export interface AuthTokens {
  jwt: string;
  /**
   * Gol (sau lipsa) cand api-ul tine refresh tokenul intr-un cookie httpOnly -
   * vezi `refresh_cookie`. Api-ul de azi il trimite mereu aici.
   */
  refresh_token?: string | null;
  /**
   * true = refresh tokenul a plecat ca cookie httpOnly (`Set-Cookie`), nu in
   * corp. Clientul nu-l mai vede si nu-l tine nicaieri; refresh-ul si logout-ul
   * pleaca atunci cu `credentials: 'include'`. Lipsa = drumul vechi.
   */
  refresh_cookie?: boolean;
  expires_in: number;
  user: ShopUser;
}

/**
 * Drepturile intoarse de `GET /shop/auth/me`.
 *
 * `approve` **nu se mai citeste de niciun ecran**. Aprobarea s-a mutat pe
 * tichet, in platforma, deci magazinul n-are niciun buton a carui desenare sa
 * depinda de el. Campul ramane tipat pentru ca api-ul il trimite in continuare,
 * si un tip care nu descrie raspunsul adevarat e mai rau decat unul cu un camp
 * nefolosit - dar nu ajunge nici in sesiune, nici in context.
 */
export interface Capabilities {
  request: boolean;
  approve: boolean;
}

export interface MeResponse {
  user: ShopUser;
  can: Capabilities;
}

export type SsoProtocol = 'oidc' | 'saml' | 'ldap';

export interface SsoProvider {
  slug: string;
  label: string;
  protocol: SsoProtocol;
  /** Absolut, duce in portal. Pagina doar trimite omul acolo. */
  start_url: string;
}

export interface SsoProvidersResponse {
  providers: SsoProvider[];
}

/* ------------------------------------------------------------ catalog, §2 */

export interface CategoryRef {
  id: number;
  name: string;
}

export interface Category extends CategoryRef {
  count: number;
}

export interface CategoriesResponse {
  categories: Category[];
}

/**
 * Pretul unui serviciu - §2 din contract.
 *
 * Forma veche era un singur `cost: Money | null`, iar `null` insemna deopotriva
 * "gratuit" si "costa dar nu se stie de aici" - de-asta tot catalogul arata "No
 * charge". Forma noua spune diferenta: `kind` distinge cele patru cazuri ca
 * valoare stabila (pagina traduce, nu se ramifica pe text), `fixed` e cifra
 * scurta pentru card, `components` e desfasurarea pentru fisa.
 */
export type CostType = 'fixed' | 'per_ci' | 'per_user' | 'per_ticket';
export type CostPeriod = 'monthly' | 'yearly';

export interface PricingComponent {
  type: CostType;
  period: CostPeriod;
  unit: Money;
}

/** Suma bazei fixe (sablon + reguli fixe), cu perioada langa bani. */
export interface FixedPrice extends Money {
  period: CostPeriod;
}

export interface Pricing {
  /** free = chiar nu costa; fixed/variable/mixed = costa. Doar `free` e "No charge". */
  kind: 'free' | 'fixed' | 'variable' | 'mixed';
  /** Moneda serviciului, `null` cand e free. Convenienta pentru card. */
  currency: string | null;
  /** Baza fixa, sau `null` cand nu exista. Cifra scurta a cardului. */
  fixed: FixedPrice | null;
  /** Fiecare componenta activa, pentru fisa. */
  components: PricingComponent[];
}

export interface CatalogItem {
  id: number;
  ci_id: number;
  name: string;
  short_description: string;
  icon: string | null;
  category: CategoryRef | null;
  /*
   * Cele doua cifre venite din contract pot LIPSI cu totul.
   *
   * Furnizorul hotaraste, in portal, daca cel care cere are voie sa vada cat
   * costa serviciul si SLA-ul lui. Cand nu are, api-ul nu trimite cheia -
   * nu trimite `null`.
   *
   * Diferenta conteaza: `sla: null` inseamna "serviciul n-are SLA", iar un
   * `pricing` lipsa ar fi ajuns, prin `pricingHeadline`, sa scrie "No charge",
   * adica exact promisiunea pe care ascunderea voia s-o evite. Lipsa cheii nu
   * se poate citi gresit: faptul nu se deseneaza deloc.
   */
  pricing?: Pricing;
  sla?: string | null;
}

/**
 * Fisa completa. Contractul spune "descriere lunga, cost, SLA si pasii", fara
 * sa fixeze numele campului de descriere lunga - de aici cele doua variante.
 */
export interface CatalogItemDetail extends CatalogItem {
  description?: string;
  long_description?: string;
  steps?: CatalogStep[];
  /** `null` cand tipul de tichet n-are flux legat; lipsa = api mai vechi. */
  workflow?: ServiceWorkflow | null;
}

/**
 * Fisa vine intr-un plic cu cheie, ca toate celelalte raspunsuri.
 *
 * Api-ul intoarce `{"service": {...}}`, la fel cum intoarce `{"categories":
 * [...]}`, `{"steps": [...]}` sau `{"providers": [...]}` - singura exceptie e
 * lista paginata, care e plata. Tipul asta lipsea, si `catalogItem()` era
 * declarat ca si cum ar intoarce serviciul direct.
 *
 * TypeScript n-avea cum sa prinda greseala: genericul de pe `http.get` e o
 * afirmatie despre corp, nu o verificare a lui. Rezultatul se vedea abia in
 * browser - `item.pricing` era `undefined` si fisa cadea la prima citire din el.
 */
export interface CatalogItemResponse {
  service: CatalogItemDetail;
}

/**
 * Si detaliul unei cereri vine in plic: `{"request": {...}}`.
 *
 * Aceeasi greseala ca la fisa serviciului, in acelasi fel: tipul lipsea si
 * apelul era declarat ca si cum ar intoarce cererea direct. Gasit uitandu-ma la
 * toate plicurile din api dupa ce fisa a cazut - nu asteptand sa cada si asta.
 */
export interface RequestDetailResponse {
  request: RequestDetail;
}

/* --------------------------------------------------- pasi si taskuri, §5 */

/** Definitia, pe intrarea de catalog. `depends_on` numeste chei din aceeasi lista. */
export interface CatalogStep {
  key: string;
  title: string;
  ticket_type_id: number;
  assigned_group_id: number | null;
  depends_on: string[];
}

export interface CatalogStepsResponse {
  steps: CatalogStep[];
}

/* ------------------------------------------- stadiile tichetului, din flux */

/**
 * Un stadiu prin care trece tichetul serviciului.
 *
 * Astea au inlocuit pasii. Pasii se scriau pe intrarea de catalog, intr-o
 * tabela care n-a ajuns niciodata in migrari; stadiile vin din fluxul tipului
 * de tichet, configurat in portal, si sunt chiar drumul pe care tichetul il
 * urmeaza.
 *
 * `type` spune unde se termina povestea: `final` inseamna ca tichetul nu mai
 * merge nicaieri de acolo - si asta e valabil si pentru refuz, nu doar pentru
 * inchidere.
 */
export interface WorkflowStage {
  status_id: number;
  name: string;
  colour: string | null;
  type: 'active' | 'resolved' | 'final';
  /** Cine preia cand tichetul intra in stadiu. `null` = ramane la cine il are. */
  goes_to: { kind: 'group' | 'person'; name: string } | null;
}

export interface ServiceWorkflow {
  name: string;
  stages: WorkflowStage[];
}

export type TaskState = 'blocked' | 'ready' | 'open' | 'done' | 'cancelled';

/** Executia. `blocked_by` poarta id-uri de TASK, nu chei de pas. */
export interface RequestTask {
  id: number;
  step_key: string;
  title: string;
  ticket_id: number | null;
  state: TaskState;
  blocked_by: number[];
  ci: { id: number; name: string } | null;
  /* Nu sunt in contract; se afiseaza doar daca api-ul le trimite. Vezi README. */
  assigned_group?: { id: number; name: string } | string | null;
  due_at?: string | null;
}

export interface RequestTasksResponse {
  tasks: RequestTask[];
}

/* ---------------------------------------------------- cos si cereri, §3 */

/**
 * Cosul. Contractul nu ii scrie campurile, deci tot ce nu e strict necesar e
 * optional si se afiseaza doar daca vine.
 */
export interface CartItem {
  id: number;
  catalog_id?: number;
  name?: string;
  catalog?: { id: number; name: string } | null;
  category?: CategoryRef | null;
  quantity?: number;
  note?: string | null;
  cost?: Money | null;
}

export interface CartPriority {
  id: number;
  name: string;
  color?: string;
}

export interface Cart {
  items: CartItem[];
  /** Nu e definit in contract. Se afiseaza doar cand vine; nu se calculeaza local. */
  total?: Money | null;
  /**
   * Prioritatile pe care le poate alege cel care cere - aceleasi cu cele din
   * Easeedesk. Vin odata cu cosul, ca formularul sa nu se deseneze cu o casuta
   * goala care se umple o secunda mai tarziu.
   */
  priorities?: CartPriority[];
}

export interface SubmitCartResponse {
  request_id: number;
}

/**
 * Tichetul deschis direct din formularul „Cerere", asa cum apare in lista.
 *
 * NU are `state` ca o cerere: o cerere trece prin starile MAGAZINULUI - cos,
 * trimisa, inchisa - iar un tichet direct nu intra niciodata in magazin. El are
 * doar starea lui de flux, care e chiar ce vrea sa stie omul.
 */
export interface DirectTicketSummary {
  kind: 'ticket';
  id: number;
  subject: string;
  type?: string | null;
  submitted_at?: string;
  updated_at?: string | null;
  status?: string | null;
  status_type?: number | null;
  /** Numele omului cand exista, altfel grupul. Vezi Views::directTicket in api. */
  assignee?: string | null;
  /**
   * Numarul celui mai nou comentariu PUBLIC scris de echipa pe tichet, sau 0.
   *
   * Nu spune daca omul l-a citit - asta se stie doar in browserul lui, si sta
   * in `lib/replies`. Neobligatoriu, ca ecranul sa mearga si cu un api mai
   * vechi: fara el, clopotelul pur si simplu nu apare.
   */
  last_staff_comment_id?: number | null;
}

export interface DirectTicketComment {
  /**
   * Comentariul prin care service desk-ul CERE fisiere.
   *
   * Numai al echipei - api-ul nu pune steagul pe raspunsurile omului. Ecranul
   * il deseneaza altfel: o cerere cu buton de atasat sub ea, nu text obisnuit.
   */
  wants_files?: boolean;
  author: string | null;
  comment: string;
  created?: string;
  /* Randul e chiar al lui. Api-ul il hotaraste - vezi contractul: se compara si
     `user_id`, si `user_type`. Neobligatoriu, ca ecranul sa mearga si cu un api
     mai vechi; lipsa se citeste ca "nu al lui", adica firul arata ca inainte. */
  mine?: boolean;
}

/**
 * Cine a cerut, aratat chiar lui.
 *
 * Fiecare camp poate lipsi: vin din `angajati`, si acolo chiar lipsesc. `null`
 * si nu sir gol, ca ecranul sa poata desena lipsa altfel decat un camp
 * completat cu nimic.
 */
export interface DirectTicketRequester {
  name: string | null;
  email: string | null;
  phone: string | null;
  /** Tichetele lui directe din ultimele sapte zile, asta inclusiv - deci >= 1. */
  tickets_7d?: number;
}

/** Un fisier legat de tichet - trimis de om sau de service desk. */
export interface TicketAttachment {
  id: number;
  name: string;
  size: number;
  is_image: boolean;
  created?: string;
}

export interface DirectTicketDetail extends DirectTicketSummary {
  description: string;
  /** Neobligatoriu, ca ecranul sa mearga si cu un api mai vechi. */
  attachments?: TicketAttachment[];
  /** Numai cele publice - `visibility = 1` in api. Notele interne nu ies. */
  comments: DirectTicketComment[];
  /* Neobligatoriu: un api mai vechi decat ecranul nu-l trimite, si atunci
     sectiunea nu se deseneaza deloc. */
  requester?: DirectTicketRequester | null;
}

export interface DirectTicketResponse {
  ticket: DirectTicketDetail;
}

/**
 * Un rand din "Cererile mele" e ori o cerere de magazin, ori un tichet direct.
 *
 * Se deosebesc dupa `kind`, nu dupa "are articole sau nu": id-urile celor doua
 * nu vin din acelasi sir, deci fara el ar fi doua lucruri diferite sub acelasi
 * numar. Cererile mai vechi din api nu poarta `kind`; lipsa lui inseamna cerere.
 */
export type FeedRow = RequestSummary | DirectTicketSummary;

export function isDirectTicket(row: FeedRow): row is DirectTicketSummary {
  return (row as DirectTicketSummary).kind === 'ticket';
}

export interface RequestSummary {
  kind?: 'request';
  id: number;
  title?: string | null;
  state?: string;
  status?: string;
  created_at?: string;
  submitted_at?: string;
  items_count?: number | null;
  items?: RequestItem[];
  /** Tichetul cererii cu un singur articol; `null` cu mai multe. Lista il scrie
      langa numar, ca „Cerere #75 · Tichet #314" sa se vada ca acelasi lucru. */
  ticket_id?: number | null;
  /* Starea tichetului, cand cererea are un singur articol; `null` pentru restul
     - cu mai multe tichete, un singur cuvant ar trebui sa aleaga unul dintre
     ele si sa le ascunda pe celelalte. Vezi Views::request() in api. */
  ticket_state?: string | null;
  /** Cand s-a miscat ultima data un tichet al cererii. `submitted_at` nu se mai
      schimba dupa trimitere, deci nu spune daca cererea mai e vie. */
  ticket_updated?: string | null;
  /** Tipul starii tichetului (`active` / `fixed` / `final`), langa `ticket_state`.
      Neobligatoriu, ca ecranul sa mearga si cu un api mai vechi. */
  ticket_state_type?: 'active' | 'fixed' | 'final' | null;
  /** Mai asteapta ceva cererea. `null` / lipsa = nu se stie; vezi lib/requestState. */
  open?: boolean | null;
}

/**
 * Tichetul unui articol, asa cum poate veni grupat sub un obiect.
 *
 * Contractul spune ca tichetul se naste la trimitere si ca pe el se face
 * aprobarea, dar §3 descrie in continuare detaliul cererii in proza, fara sa
 * fixeze numele campurilor. Formele acceptate sunt scrise aici o singura data,
 * iar `lib/ticket.ts` e singurul loc care alege intre ele.
 */
export interface TicketInfo {
  id?: number | null;
  /** Starea din fluxul tipului de tichet: "Pending Approval", "Approved", … */
  state?: string | null;
  status?: string | null;
  /** Eticheta gata scrisa, daca api-ul o trimite; altfel se scrie din `state`. */
  state_label?: string | null;
  /** Adresa absoluta catre tichet in platforma, daca api-ul o cunoaste. */
  url?: string | null;
  /**
   * Cine tine tichetul acum, si de cand.
   *
   * `null` cand nu se stie inca - lipsa e o informatie, si se deseneaza altfel
   * decat un nume. Starea singura nu raspunde la intrebarea cu care omul intra
   * pe cerere: cine se ocupa, si mai am de asteptat?
   */
  assignee?: string | null;
  group?: string | null;
  updated?: string | null;
}

export interface RequestItem {
  id: number;
  name?: string;
  catalog?: { id: number; name: string } | null;
  quantity?: number;
  note?: string | null;
  cost?: Money | null;
  state?: string;
  status?: string;
  /* Tichetul articolului: fie grupat, fie desfacut pe campuri. Toate sunt
     optionale - un camp care lipseste nu se deseneaza, nu se inlocuieste cu o
     presupunere. */
  ticket?: TicketInfo | null;
  ticket_id?: number | null;
  ticket_state?: string | null;
  ticket_status?: string | null;
  ticket_state_label?: string | null;
  ticket_url?: string | null;
  /* Tichetele deschise sub cel al articolului, pentru serviciile de care
     depinde. Api-ul trimite sir gol cand nu sunt, dar campul ramane optional:
     o instalare mai veche nu-l trimite deloc. */
  children?: RequestChildTicket[];
}

/**
 * Un tichet copil al articolului.
 *
 * Nu e un articol: nimeni nu l-a pus in cos, s-a deschis fiindca serviciul
 * cerut depinde de altul. De-asta are numai ce trebuie ca sa fie aratat si
 * deschis - numar, subiect, stare - si nu cantitate, cost sau nota.
 */
export interface RequestChildTicket {
  id: number;
  subject?: string;
  status?: { id: number; name: string; type: string } | null;
}

export interface RequestLogEntry {
  created_at?: string;
  at?: string;
  date?: string;
  user?: { name?: string; email?: string } | string | null;
  author?: string;
  message?: string;
  text?: string;
  action?: string;
}

/**
 * Detaliul unei cereri. Contractul spune "cerere, articolele ei, jurnalul si
 * taskurile", fara sa spuna daca cererea e la radacina sau sub `request`.
 */
export interface RequestDetail extends RequestSummary {
  request?: RequestSummary;
  requester?: { name?: string; email?: string } | string | null;
  user?: { name?: string; email?: string } | string | null;
  /* `events` e numele pe care il trimite api-ul azi; celelalte raman ca
     variante, fiindca jurnalul nu e fixat in contract. */
  events?: RequestLogEntry[];
  log?: RequestLogEntry[];
  journal?: RequestLogEntry[];
  history?: RequestLogEntry[];
  tasks?: RequestTask[];
}

/*
 * §4 - aprobari: nu exista tip aici, si nu e o scapare.
 *
 * Aprobarea nu mai are rute si nu mai are ecran; e o mutare de stare pe tichet.
 * Ce se vedea odata pe ecranul de aprobari se vede acum pe tichet, iar ce
 * trebuie sa stie magazinul despre tichet e in `TicketInfo`, pe articol.
 */

/* ---------------------------------------------------- tichet direct, §8 */

/** Un tip dintre care se poate alege in formularul „Cerere". */
export interface TicketType {
  id: number;
  name: string;
}

export interface TicketTypesResponse {
  types: TicketType[];
}

export interface CreateTicketResponse {
  ticket_id: number;
}

/* ------------------------------------------------------- 9. autoservire */

/**
 * Un articol asa cum apare in rezultatele cautarii: fara corp.
 *
 * Corpul vine numai de la `GET /shop/help/{id}`. Trimis in lista, patru
 * articole ar fi insemnat patru pagini de HTML aduse ca sa se afiseze patru
 * titluri.
 */
export interface HelpArticleSummary {
  id: number;
  title: string;
  summary: string;
  lang: string;
  /**
   * Minutele, ca NUMAR - fraza se compune aici, in pagina.
   *
   * `null` inseamna "nimeni n-a masurat", si atunci nu se spune nimic. Nu se
   * poate confunda cu zero, si asta e intentionat: un zero ar fi promis
   * rezolvare instantanee pentru un camp pe care adminul nu l-a completat.
   */
  self_serve_minutes: number | null;
  category: CategoryRef | null;
}

export interface HelpArticle extends HelpArticleSummary {
  /** HTML, scris de operatorii portalului in editorul lui. */
  body: string;
  updated_at: string | null;
}

/**
 * Baza de cunostinte RASFOITA: paginata, ca lista de catalog.
 *
 * `GET /shop/help` era pana acum o cautare cu alt nume - cerea `q` si fara el
 * intorcea gol. Acum un `q` gol intoarce tot ce exista, fiindca altfel nu
 * exista nicio cale de a AJUNGE la un articol fara sa-l nimeresti dintr-o
 * cautare.
 */
export type HelpListResponse = Paginated<HelpArticleSummary> & { query: string };

export interface HelpArticleResponse {
  article: HelpArticle;
}

/**
 * Ce a intors caseta: doua liste SEPARATE, niciodata una amestecata.
 *
 * Catalogul raspunde la "vreau sa cer ceva", articolele la "ceva nu merge".
 * Amestecate si ordonate dupa scor, omul n-ar mai sti ce e o comanda si ce e o
 * instructiune - ar apasa pe un articol crezand ca a cerut un laptop.
 */
export interface SearchResponse {
  /** Randul din jurnal, pentru `searchOutcome`. `0` = n-a putut fi notat. */
  search_id: number;
  query: string;
  articles: HelpArticleSummary[];
  services: CatalogItem[];
}

/** Ce a facut omul dupa ce a cautat. Singura masura care conteaza. */
export type SearchOutcome = 'nothing' | 'article' | 'cart' | 'ticket';
